import React, { useEffect, useState } from "react";
import { Delete, Eye, Pen, Plus } from "lucide-react";
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { createProduct, deleteProduct, getProducts, updateProduct } from "../../../slices/company/rawProductSlice";
import AddPurchases from "./add_purchases";
import { fetchRawCategories } from "../../../slices/company/rawCategorySlice";
import { fetchSuppliers } from "../../../slices/company/supplierSlice";
import { fetchBrands } from "../../../slices/company/brandSlice";
import { API_URL } from "../../../utils/constant";

import VendorPurchaseData from "./purchase_vendors_data";
import RawProductDetailModal from "./raw_product_details";
import { customTableStyles } from "../../../utils/tableStylings";
import { useLocation } from "react-router-dom";

const Purchases = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [modalHeading, setModalHeading] = useState('');
    
    // Search states
    const [searchProductName, setSearchProductName] = useState('');
    const [searchArticleNo, setSearchArticleNo] = useState('');
    const [searchVendor, setSearchVendor] = useState('');
    const [searchCategory, setSearchCategory] = useState('');
    
    // Pagination states
    const [paginationPage, setPaginationPage] = useState(1);
    const [paginationPerPage, setPaginationPerPage] = useState(50);
    
    const { rawProducts, totalCount, totalPages, currentPage, potencies, volumes } = useSelector(state => state.rawProduct);
    const { rawCategories } = useSelector(state => state.rawCategories);
    const { suppliers } = useSelector((state) => state.suppliers);
    const [productsLoading, setProductsLoading] = useState(true);
    const { brands } = useSelector(state => state.brands);

    const [inputProductName, setInputProductName] = useState('');
    const [inputCategory, setInputCategory] = useState('');
    const [inputBrand, setInputBrand] = useState('');
    const [inputPotency, setInputPotency] = useState('');
    const [inputVolume, setInputVolume] = useState('');

    // Applied filters (sent to API)
    const [appliedFilters, setAppliedFilters] = useState({
        productName: '',
        categoryId: '',
        brandId: '',
        potency: '',
        volume: '',
    });


    useEffect(() => {
        const company = JSON.parse(localStorage.getItem('company'));
        if (company) {
            dispatch(fetchSuppliers(company.company?.id));
        }
    }, [dispatch]);

    // Fetch products with pagination
    useEffect(() => {
        const company = JSON.parse(localStorage.getItem('company'));
        if (company) {
            const companyId = company.company?.id;
            setProductsLoading(true);
            dispatch(getProducts({ 
                companyId, 
                page: paginationPage, 
                limit: paginationPerPage,
                filters: appliedFilters
            })).finally(() => {
                setProductsLoading(false);
            });

            dispatch(fetchRawCategories(companyId));
            dispatch(fetchBrands(companyId));
        }
    }, [dispatch, paginationPage, paginationPerPage, appliedFilters]);

    const handlePageChange = (page) => {
        setPaginationPage(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPaginationPerPage(newPerPage);
        setPaginationPage(page);
    };

     const handleFilterSubmit = (e) => {
        e.preventDefault();
        setAppliedFilters({
            productName: inputProductName,
            categoryId: inputCategory,
            brandId: inputBrand,
            potency: inputPotency,
            volume: inputVolume,
        });
        setPaginationPage(1); // Reset to page 1 when filters are applied
    };

    const handleClearFilters = () => {
        setInputProductName('');
        setInputCategory('');
        setInputBrand('');
        setInputPotency('');
        setInputVolume('');
        setAppliedFilters({
            productName: '',
            categoryId: '',
            brandId: '',
            potency: '',
            volume: '',
        });
        setPaginationPage(1);
    };

    const handleFormSubmit = (formData) => {
        if (selectedProduct && selectedProduct.id != null) {
            dispatch(updateProduct({ id: selectedProduct.id, productData: formData }));
        } else {
            dispatch(createProduct(formData));
        }
        setShowModal(false);
        window.location.reload();
    };

    const handleVendorSubmit = (formData) => {
        window.location.reload();
        setShowVendorModal(false);
    };

    const handleEditTax = (productData) => {
        setSelectedProduct(productData);
        setModalHeading('Edit Product');
        setShowModal(true);
    };
    
    const handleEditVendor = (productData) => {
        setSelectedProduct(productData);
        setModalHeading('Select Vendor & Price');
        setShowVendorModal(true);
    };
    
    const handleEditDetail = (productData) => {
        setSelectedProduct(productData);
        setModalHeading('More Details');
        setShowDetailModal(true);
    };

    const handleDelete = (taxId) => {
        Swal.fire({
            title: '<strong>Are you sure?</strong>',
            html: `<p>This action will <b>permanently delete</b> this Product.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteProduct(taxId));
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'The Product has been deleted.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    didOpen: (toast) => {
                        toast.addEventListener('mouseenter', Swal.stopTimer);
                        toast.addEventListener('mouseleave', Swal.resumeTimer);
                    }
                });
            }
        });
    };

    const productsColumn = [
        {
            name: '#',
            cell: (row, index) => (currentPage - 1) * paginationPerPage + index + 1,
            width: '50px'
        },
        {
            name: 'Image',
            cell: row => (
                <img
                    src={`${API_URL}${row.product_image}`}
                    alt={row.name}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = './logo.png';
                    }}
                    style={{ height: '50px', width: "50px", objectFit: 'cover' }}
                />
            ),
            width: '100px',
            sortable: true
        },
        {
            name: 'Product Name',
            selector: row => row.name,
            sortable: true
        },
        {
            name: 'Article No/Name',
            selector: row => row.sku,
            sortable: true
        },
        {
            name: 'Brand',
            selector: row => row.Brand?.name || 'OTHER',
            sortable: true
        },
        {
            name: 'Category',
            selector: row => row.RawCategory?.name,
            sortable: true
        },
        {
            name: 'Supplier',
            selector: row => {
                if (Array.isArray(row.RawProductVendors) && row.RawProductVendors.length > 0) {
                    const vendors = row.RawProductVendors.length;
                    return `${vendors} Suppliers`;
                }
                return 'N/A';
            },
            sortable: true
        },
        {
            name: 'Unit',
            selector: row => row.RawCategory?.Unit?.name,
            sortable: true
        },
        {
            name: 'Quantity',
            selector: row => {
                if (row.product_type == 'simple') {
                    return row.quantity;
                } else if (Array.isArray(row.rawMaterialVariantStock)) {
                    return row.rawMaterialVariantStock.length;
                }
                return 0;
            },
            sortable: true
        },
        {
            name: 'Actions',
            cell: row => (
                <>
                    <span
                        className=" btn btn-primary btn-sm text-white cursor-pointer me-2"
                        onClick={() => handleEditDetail(row)}
                    >
                        <Eye />
                    </span>
                    <span
                        className=" btn btn-primary btn-sm text-white cursor-pointer me-2"
                        onClick={() => handleEditVendor(row)}
                    >
                        <Plus />
                    </span>
                    <span
                        className=" btn btn-warning btn-sm text-white cursor-pointer me-2"
                        onClick={() => handleEditTax(row)}
                    >
                        <Pen />
                    </span>
                    <span
                        className=" btn btn-danger btn-sm text-white cursor-pointer"
                        onClick={() => handleDelete(row.id)}
                    >
                        <Delete />
                    </span>
                </>
            ),
            width: '220px'
        }
    ];

    useEffect(() => {
        if (location.state?.openModal) {
            handleAdd();
        }
    }, [location]);

    const handleAdd = () => {
        setSelectedProduct(null);
        setModalHeading('Create New Product');
        setShowModal(true);
    };

    // Client-side filtering (works on current page only with server-side pagination)
    const filteredProducts = rawProducts.filter(row => {
        if (searchProductName && !(row.name && row.name.toLowerCase().includes(searchProductName.toLowerCase()))) {
            return false;
        }
        if (searchArticleNo && !(row.sku && row.sku.toLowerCase().includes(searchArticleNo.toLowerCase()))) {
            return false;
        }
        if (searchVendor) {
            if (Array.isArray(row.RawProductVendors) && row.RawProductVendors.length > 0) {
                const vendorMatch = row.RawProductVendors.some(vendor => 
                    vendor.Supplier?.name && vendor.Supplier.name.toLowerCase().includes(searchVendor.toLowerCase())
                );
                if (!vendorMatch) return false;
            } else {
                return false;
            }
        }
        if (searchCategory && !(row.RawCategory?.name && row.RawCategory.name.toLowerCase().includes(searchCategory.toLowerCase()))) {
            return false;
        }
        return true;
    });

    return (
        <>
            <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h3 className="text-primary mb-0">Item Purchased Master</h3>
                    <button
                        className="btn btn-primary"
                        onClick={() => handleAdd()}
                    >
                        + Add New
                    </button>
                </div>
                <div className="card-header">
                    <h6 className="card-title mb-0">Search Products</h6>
                    {/* <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                            <div className="position-relative mt-3 mb-2">
                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                    Product Name
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Product Name"
                                    value={searchProductName}
                                    onChange={e => setSearchProductName(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="position-relative mt-3 mb-2">
                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                    Article Number
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Article Number"
                                    value={searchArticleNo}
                                    onChange={e => setSearchArticleNo(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="position-relative mt-3 mb-2">
                                <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                    Category
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Category"
                                    value={searchCategory}
                                    onChange={e => setSearchCategory(e.target.value)}
                                />
                            </div>
                        </div>
                    </div> */}

                    <form onSubmit={handleFilterSubmit}>
                        <div className="row g-2 align-items-end">
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <div className="position-relative mt-3 mb-2">
                                    <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                        Product Name
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Product Name"
                                        value={inputProductName}
                                        onChange={e => setInputProductName(e.target.value)}
                                        // value={searchProductName}
                                        // onChange={e => setSearchProductName(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <div className="position-relative mt-3 mb-2">
                                    <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                        Category
                                    </span>
                                    <select
                                        className="form-control"
                                        value={inputCategory}
                                        onChange={e => setInputCategory(e.target.value)}
                                        // value={searchCategory}
                                        // onChange={e => setSearchCategory(e.target.value)}
                                    >
                                        <option value="">All Categories</option>
                                        {rawCategories.map(cat => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <div className="position-relative mt-3 mb-2">
                                    <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                        Brand
                                    </span>
                                    <select
                                        className="form-control"
                                        value={inputBrand}
                                        onChange={e => setInputBrand(e.target.value)}
                                        // value={searchBrand}
                                        // onChange={e => setSearchBrand(e.target.value)}
                                    >
                                        <option value="">All Brands</option>
                                        {brands.map(brand => (
                                            <option key={brand.id} value={brand.id}>
                                                {brand.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <div className="position-relative mt-3 mb-2">
                                    <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                        Potency
                                    </span>
                                    <select
                                        className="form-control"
                                        value={inputPotency}
                                        onChange={e => setInputPotency(e.target.value)}
                                        // value={searchPotency}
                                        // onChange={e => setSearchPotency(e.target.value)}
                                    >
                                        <option value="">All Potencies</option>
                                        {potencies.map((pot, pte) => (
                                            <option key={pte} value={pot.potency}>
                                                {pot.potency}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <div className="position-relative mt-3 mb-2">
                                    <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                        Volume
                                    </span>
                                    <select
                                        className="form-control"
                                        value={inputVolume}
                                        onChange={e => setInputVolume(e.target.value)}
                                        // value={searchVolume}
                                        // onChange={e => setSearchVolume(e.target.value)}
                                    >
                                        <option value="">All Volumes</option>
                                        {volumes.map((vol,  vle) => (
                                            <option key={vle} value={vol.volume}>
                                                {vol.volume}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <div className="d-flex gap-2">
                                    <button type="submit" className="btn btn-primary flex-fill">
                                        Apply Filters
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary"
                                        onClick={handleClearFilters}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
                <div className="card-body">
                    <DataTable
                        columns={productsColumn}
                        data={filteredProducts}
                        pagination
                        paginationServer
                        paginationTotalRows={totalCount}
                        paginationPerPage={paginationPerPage}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        highlightOnHover
                        striped
                        dense
                        progressPending={productsLoading}
                        customStyles={customTableStyles}
                    />
                </div>
            </div>

            <RawProductDetailModal show={showDetailModal} onClose={() => setShowDetailModal(false)} heading={modalHeading} initialData={selectedProduct} categories={rawCategories} suppliers={suppliers} />
            <VendorPurchaseData show={showVendorModal} onClose={() => setShowVendorModal(false)} heading={modalHeading} initialData={selectedProduct} onSubmit={handleVendorSubmit} categories={rawCategories} suppliers={suppliers} />
            <AddPurchases show={showModal} onClose={() => setShowModal(false)} heading={modalHeading} initialData={selectedProduct} onSubmit={handleFormSubmit} categories={rawCategories} suppliers={suppliers} />
        </>
    );
}

export default Purchases;